/**
 * UTM + click-ID + observed-cookie capture, and first/last-touch (design §3, §2.3).
 * First touch is write-once. Last touch refreshes only when new campaign signals
 * are present (direct/internal nav does not clobber a prior paid touch — §27.16).
 */
import type { PixelStore } from './storage';
import type { TouchContext } from './types';
import {
  getCookie,
  isSameOrigin,
  nowIso,
  parseQuery,
  safeJsonParse,
  safeJsonStringify,
  urlWithoutQueryOrFragment,
} from './util';

/** Click-ID query params → externalIds key. */
const CLICK_IDS: Record<string, string> = {
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbclid: 'fbclid',
  msclkid: 'msclkid',
  li_fat_id: 'liFatId',
  ttclid: 'ttclid',
  twclid: 'twclid',
  dclid: 'dclid',
  rdt_cid: 'rdt_cid',
};

export interface CampaignSnapshot {
  utm: Record<string, string>;
  clickIds: Record<string, string>;
  externalIds: Record<string, string>;
}

export function parseCampaign(customClickIds?: string[], marketingAllowed = false): CampaignSnapshot {
  const utm: Record<string, string> = {};
  const clickIds: Record<string, string> = {};
  const externalIds: Record<string, string> = {};
  try {
    const q = parseQuery(location.search);
    const map: Record<string, string> = {
      utm_source: 'source',
      utm_medium: 'medium',
      utm_campaign: 'campaign',
      utm_content: 'content',
      utm_term: 'term',
    };
    for (const k in map) if (q[k]) utm[map[k]] = q[k];

    // Click IDs are advertising identifiers. Do not even capture them until
    // the CMP has granted marketing consent.
    if (marketingAllowed) {
      const ids = { ...CLICK_IDS };
      if (customClickIds) for (const c of customClickIds) ids[c] = c;
      for (const param in ids) {
        if (q[param]) {
          clickIds[ids[param]] = q[param];
          externalIds[ids[param]] = q[param];
        }
      }
    }
  } catch {
    /* noop */
  }
  // Observed FIRST-PARTY platform cookies (never written by us) are also
  // advertising/platform identifiers and remain inaccessible before consent.
  if (marketingAllowed) {
    observe(externalIds, 'fbp', getCookie('_fbp'));
    observe(externalIds, 'fbc', getCookie('_fbc'));
    observe(externalIds, 'ttp', getCookie('_ttp'));
    const ga = getCookie('_ga');
    if (ga) {
      const m = ga.match(/^GA\d\.\d\.(.+)$/);
      if (m) externalIds.gaClientId = m[1];
    }
  }
  return { utm, clickIds, externalIds };
}

function observe(target: Record<string, string>, key: string, value: string | null): void {
  if (value) target[key] = value;
}

export interface TouchResult {
  firstTouch?: TouchContext;
  lastTouch?: TouchContext;
  utmInternalReferrer: boolean;
}

/** Compute/persist first & last touch for the current landing. */
export function captureTouches(store: PixelStore, snap: CampaignSnapshot): TouchResult {
  const hasSignal =
    Object.keys(snap.utm).length > 0 ||
    Object.keys(snap.clickIds).length > 0 ||
    externalReferrer();

  const current: TouchContext = {
    source: snap.utm.source,
    medium: snap.utm.medium,
    campaign: snap.utm.campaign,
    content: snap.utm.content,
    term: snap.utm.term,
    landingUrl: safeUrl(),
    referrer: safeRef(),
    ts: nowIso(),
    clickIds: Object.keys(snap.clickIds).length ? snap.clickIds : undefined,
  };

  // First touch — write once.
  let firstTouch = safeJsonParse<TouchContext>(store.readFirstTouch()) || undefined;
  if (!firstTouch) {
    firstTouch = current;
    store.writeFirstTouch(safeJsonStringify(current));
  }

  // Last touch — refresh only when this landing carries campaign signals.
  let lastTouch = safeJsonParse<TouchContext>(store.readLastTouch()) || undefined;
  if (hasSignal) {
    lastTouch = current;
    store.writeLastTouch(safeJsonStringify(current));
  } else if (!lastTouch) {
    lastTouch = firstTouch;
  }

  const utmInternalReferrer = Object.keys(snap.utm).length > 0 && internalReferrer();
  return { firstTouch, lastTouch, utmInternalReferrer };
}

function safeUrl(): string {
  try {
    return urlWithoutQueryOrFragment(location.href);
  } catch {
    return '';
  }
}
function safeRef(): string {
  try {
    return urlWithoutQueryOrFragment(document.referrer || '');
  } catch {
    return '';
  }
}
function externalReferrer(): boolean {
  const r = safeRef();
  return !!r && !isSameOrigin(r);
}
function internalReferrer(): boolean {
  const r = safeRef();
  return !!r && isSameOrigin(r);
}
